<template>
  <section class="basket-sidebar-wrapper">
    <b-sidebar
      id="basket-sidebar"
      :key="`${value}_${bundleCount}`"
      :visible="showBasket"
      :width="windowWidth >= 550 ? '500px' : `${windowWidth * 0.9}px`"
      backdrop
      bg-variant="white"
      header-class="d-block border-bottom"
      lazy
      right
      sidebar-class="basket-sidebar"
      role="dialog"
      aria-labelledby="sidebar-title"
      aria-modal="true"
      @hidden="hideBasket"
    >
      <template #header="{ hide }">
        <div class="d-flex text-center text-secondary pt-2 pb-0">
          <b-button-close v-testid="'sidebar-close-action'" @click="hide" />
          <div class="w-100 font-weight-bold">
            <h1 v-testid="'sidebar-item-count-message'" class="h3">
              <strong id="sidebar-title">{{ $t('basket.yourBasket') }}</strong> ({{ order.itemsCount || 0 }}
              {{ (order.itemsCount || 0) > 1 ? 'items' : 'item' }}): <strong>{{ $n(order.total || 0, 'currency') }}</strong>
            </h1>
            <h2 v-if="order.itemsCount > 0" class="text-secondary text-center h4">
              <b-link href="javascript:void(0)" :aria-label="$t('dp.buttons.checkoutAriaLabel')" @click="checkoutClick"
                ><u>{{ $t('dp.buttons.checkOut') }}</u></b-link
              >
            </h2>
          </div>
        </div>
      </template>

      <template #default="{ hide }">
        <dp-loading v-if="orderLoading" />
        <section v-else v-testid="'sidebar-ready'" class="px-3 mt-3">
          <section v-if="!order || !order.items || !order.items.length" id="basket-empty" v-testid="'sidebar-empty'" class="text-center">
            <div>
              <fa-icon :icon="['far', 'shopping-basket']" />
              <h2>{{ $t('sideBasket.emptyBasket') }}</h2>
            </div>
          </section>

          <section v-else>
            <section v-if="pets && pets.length" id="basket-pets">
              <div v-for="pet in pets" :key="pet.id" v-testid="{ id: 'sidebar-pet-group', key: pet.id }" class="mb-4">
                <pet-header :pet="pet" class="mb-2" variant="basket" />
                <product-header v-if="sections.pets[pet.id].licenses.length">
                  <template #icon>
                    <fa-icon :icon="['far', 'address-card']" class="icon mr-1" />
                  </template>
                  <template #title>{{ $t('basket.licenseTitle') }}</template>
                </product-header>
                <product
                  v-for="(item, index) in sections.pets[pet.id].licenses"
                  :key="item.id"
                  :item="item"
                  :last="index + 1 === sections.pets[pet.id].licenses.length"
                  :licensing-total="licensingTotalByPet(pet.id)"
                  :locale="locale"
                  :order="order"
                  :order-id="orderId"
                  :remove-item-state="removeItemState"
                  @reloadSideBasket="loadBasket(null, true)"
                  @removeItem="onRemoveItem"
                />
                <product-header v-if="sections.pets[pet.id].replacements.length">
                  <template #icon>
                    <fa-icon :icon="['far', 'sync-alt']" class="icon mr-1" />
                  </template>
                  <template #title>{{ $t('basket.replacementTitle') }}</template>
                </product-header>
                <product
                  v-for="(item, index) in sections.pets[pet.id].replacements"
                  :key="item.id"
                  :item="item"
                  :last="index + 1 === sections.pets[pet.id].replacements.length"
                  :locale="locale"
                  :order="order"
                  :order-id="orderId"
                  :remove-item-state="removeItemState"
                  @reloadSideBasket="loadBasket(null, true)"
                  @removeItem="onRemoveItem"
                />
                <div
                  v-if="containFreePetTagWithReplacementFee(pet.id)"
                  class="px-1.5 py-2 bg-warning-light rounded col-12 align-self-center text-primary my-2"
                >
                  {{ freePetTagGroupNotification(pet.id) }}
                </div>

                <product-header v-if="sections.pets[pet.id].tags.length" v-testid="'sidebar-pet-tags-header-region'">
                  <template #icon>
                    <fa-icon :icon="['far', 'tags']" class="icon mr-1" />
                  </template>
                  <template #title>{{ $t('basket.tagTitle') }}</template>
                </product-header>
                <product
                  v-for="(item, index) in sections.pets[pet.id].tags"
                  :key="item.id"
                  :add-item-state="addItemState"
                  :class="{ 'pb-3': sections.pets[pet.id].collars.length }"
                  :connector-products="connectorProducts"
                  :edit="editTag(item, pet)"
                  :glow-tag-products="glowTagProducts"
                  :item="item"
                  :last="index + 1 === sections.pets[pet.id].tags.length"
                  :locale="locale"
                  :no-new-tag="noNewTag(item)"
                  :order="order"
                  :order-id="orderId"
                  :remove="removeTag(item, pet)"
                  :remove-item-state="removeItemState"
                  @addChildItem="onAddChildItem"
                  @reloadSideBasket="loadBasket(null, true)"
                  @removeItem="onRemoveItem"
                  @remove-bundle="$emit('remove-bundle')"
                />
                <div class="mt-3">
                  <product-header
                    v-if="
                      [
                        ...sections.pets[pet.id].collars,
                        ...sections.pets[pet.id].petTrackingDevices,
                        ...sections.pets[pet.id].subscriptions,
                      ].length
                    "
                    v-testid="'sidebar-pet-products-header-region'"
                  >
                    <template #icon> <fa-icon :icon="['far', 'shopping-basket']" class="icon mr-1" /> </template>
                    <template #title>{{ $t('basket.petProductTitle') }}</template>
                  </product-header>
                  <dp-product-messaging v-if="sections.pets[pet.id].petTrackingDevices.length">
                    <template #icon>
                      <svg width="33" height="28" viewBox="0 0 33 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M14.5995 27.303L3.04397 16.5148C2.77539 16.2654 2.51959 15.9968 2.28298 15.7218H7.84654C9.29178 15.7218 10.5963 14.8521 11.1527 13.5156L11.8242 11.904L14.9768 18.9065C15.2199 19.45 15.7506 19.8017 16.3454 19.8081C16.9401 19.8145 17.4836 19.4884 17.7522 18.9576L20.4637 13.5283L20.5724 13.7458C21.1799 14.9608 22.4205 15.7282 23.7762 15.7282H30.4589C30.2223 16.0032 29.9665 16.2718 29.6979 16.5212L18.1423 27.303C17.6627 27.7506 17.0296 28 16.3709 28C15.7123 28 15.0792 27.7506 14.5995 27.303ZM32.2111 12.6522H23.7698C23.578 12.6522 23.3989 12.5435 23.3094 12.3709L21.8258 9.41003C21.5636 8.89205 21.0328 8.55951 20.4509 8.55951C19.8689 8.55951 19.3382 8.88565 19.076 9.41003L16.4285 14.705L13.1671 7.42122C12.9177 6.86486 12.3549 6.50675 11.7474 6.51954C11.1399 6.53233 10.5899 6.89684 10.3533 7.46598L8.31976 12.3453C8.24302 12.5371 8.05118 12.6586 7.84654 12.6586H1.02318C0.856916 12.6586 0.703439 12.6842 0.556356 12.729C0.191847 11.7058 0 10.6187 0 9.51235V9.14145C0 4.67142 3.22942 0.860056 7.63551 0.124643C10.5516 -0.361369 13.5188 0.591471 15.6035 2.67621L16.3709 3.44359L17.1383 2.67621C19.2231 0.591471 22.1903 -0.361369 25.1064 0.124643C29.5124 0.860056 32.7419 4.67142 32.7419 9.14145V9.51235C32.7419 10.5931 32.5628 11.6546 32.2111 12.6522Z"
                          fill="#003C71"
                        />
                      </svg>
                    </template>
                    <template #message>
                      <span v-html="$t('basket.petTrackerAddedMessaging', { petName: pet.name })"></span>
                    </template>
                  </dp-product-messaging>
                  <product
                    v-for="(item, index) in [
                      ...sections.pets[pet.id].subscriptions,
                      ...sections.pets[pet.id].collars,
                      ...sections.pets[pet.id].petTrackingDevices,
                    ]"
                    :key="item.id"
                    :item="item"
                    :last="
                      index + 1 ===
                      [
                        ...sections.pets[pet.id].subscriptions,
                        ...sections.pets[pet.id].collars,
                        ...sections.pets[pet.id].petTrackingDevices,
                      ].length
                    "
                    :locale="locale"
                    :no-new-tag="false"
                    :order="order"
                    :order-id="orderId"
                    :remove="removeTag(item, pet)"
                    :remove-item-state="removeItemState"
                    @reloadSideBasket="loadBasket(null, true)"
                    @removeItem="onRemoveItem"
                  />
                </div>
                <hr />
                <template v-for="(nextPet, idx) in [nextAccountPetWithoutTag(pet.id)]">
                  <NextPetTagCTA
                    v-if="nextPet"
                    :key="`cta-${idx}`"
                    :pet="nextPet"
                  ></NextPetTagCTA>
                  <pet-footer
                    v-else
                    :key="`footer-${idx}`"
                    :bundle-count="bundleCountByPetId[pet.id]"
                    :order-id="orderId"
                    :pet="pet"
                  ></pet-footer>
                </template>

                <div v-if="showWalletCardProducts && hasWalletCardItemsOrConfiguration(pet.id)">
                  <product-header class="mt-2">
                    <template #icon>
                      <fa-icon :icon="['far', 'address-card']" class="icon mr-1" />
                    </template>
                    <template #title>{{ $t('basket.walletCard.title') }}</template>
                  </product-header>
                  <template>
                    <product
                      v-for="(item, index) in sections.pets[pet.id].walletcards"
                      :key="item.id"
                      :class="{ 'pb-3': sections.pets[pet.id].collars.length }"
                      :item="item"
                      :last="index + 1 === sections.pets[pet.id].walletcards.length"
                      :locale="locale"
                      :no-new-tag="false"
                      :order="order"
                      :order-id="orderId"
                      :remove-item-state="removeItemState"
                      remove
                      wallet-card
                      @reloadSideBasket="loadBasket(null, true)"
                      @removeItem="onRemoveItem"
                      @remove-bundle="$emit('remove-bundle')"
                    />
                  </template>
                  <configure-wallet-card
                    v-for="({ orderItemBundle }, index) in incompleteWalletCardConfigurations(pet.id)"
                    :key="`incomplete-wallet-card-configuration-${index}`"
                    :locale="locale"
                    :order="order"
                    :order-id="order.id || orderId"
                    :order-item-bundle="orderItemBundle"
                    :pet-id="pet.id"
                    :pet-name="pet.name"
                    :remove-item-state="removeItemState"
                    :wallet-card-complete="false"
                    remove
                    @remove-bundle="$emit('remove-bundle')"
                  />
                </div>
              </div>
            </section>

            <section v-if="sections.essentials.length || sections.miscellaneous.length" id="miscellaneous-products" class="mb-4">
              <product-header>
                <template #icon>
                  <fa-icon :icon="['far', 'shopping-basket']" class="icon mr-1" />
                </template>
                <template #title>Products</template>
              </product-header>
              <product
                v-for="(item, index) in [...sections.essentials, ...sections.miscellaneous]"
                :key="item.id"
                :connector-products="connectorProducts"
                :glow-tag-products="glowTagProducts"
                :item="item"
                :last="index + 1 === [...sections.essentials, ...sections.miscellaneous].length"
                :locale="locale"
                :order="order"
                :order-id="orderId"
                :remove-item-state="removeItemState"
                additional
                remove
                @addChildItem="onAddChildItem"
                @reloadSideBasket="loadBasket(null, true)"
                @removeItem="onRemoveItem"
              />
              <hr />
            </section>

            <section v-if="sections.walletCardConfigurations.length" class="mb-4">
              <product-header>
                <template #icon>
                  <fa-icon :icon="['far', 'address-card']" class="icon mr-1" />
                </template>
                <template #title>{{ $t('basket.walletCard.title') }}</template>
              </product-header>
              <configure-wallet-card
                v-for="({ orderItemBundle }, index) in sections.walletCardConfigurations"
                :key="`incomplete-wallet-card-configuration-${index}`"
                :locale="locale"
                :order="order"
                :order-id="order.id || orderId"
                :order-item-bundle="orderItemBundle"
                :remove-item-state="removeItemState"
                :wallet-card-complete="false"
                @remove-bundle="$emit('remove-bundle')"
              />
              <hr />
            </section>

            <section v-if="sections.additional.length" id="additional-products" class="mb-4">
              <product-header>
                <template #icon>
                  <fa-icon :icon="['far', 'money-check-edit-alt']" class="icon mr-1" />
                </template>
                <template #title>{{ $t('basket.additionalFeesTitle') }}</template>
              </product-header>
              <product
                v-for="(item, index) in sections.additional"
                :key="item.id"
                :connector-products="connectorProducts"
                :glow-tag-products="glowTagProducts"
                :item="item"
                :last="index + 1 === sections.additional.length"
                :locale="locale"
                :order="order"
                :order-id="orderId"
                :remove-item-state="removeItemState"
                additional
                @addChildItem="onAddChildItem"
                @reloadSideBasket="loadBasket(null, true)"
                @removeItem="onRemoveItem"
              />
              <hr />
            </section>

            <section v-if="sections.donations.length" id="donation-products" class="mb-4">
              <product-header>
                <template #icon>
                  <fa-icon :icon="['far', 'heart']" class="icon mr-1" />
                </template>
                <template #title>{{ $t('basket.donationTitle') }}</template>
              </product-header>
              <product
                v-for="(item, index) in sections.donations"
                :key="item.id"
                :item="item"
                :last="index + 1 === sections.donations.length"
                :locale="locale"
                :order="order"
                :order-id="orderId"
                :remove-item-state="removeItemState"
                remove
                @reloadSideBasket="loadBasket(null, true)"
                @removeItem="onRemoveItem"
              />
              <hr />
            </section>

            <section v-if="sections.vs.pet.length || sections.vs.petOwner.length" id="vs-products">
              <h2 class="mb-2">
                <strong>{{ $t('basket.vsTitle') }}</strong>
              </h2>
              <product-header v-if="sections.vs.pet.length">
                <template #icon>
                  <fa-icon :icon="['far', 'eye']" class="icon mr-1" />
                </template>
                <template #title>{{ $t('basket.vsPetTitle') }}</template>
              </product-header>
              <product
                v-for="(item, index) in sections.vs.pet"
                :key="item.id"
                :item="item"
                :last="index + 1 === sections.vs.pet.length"
                :locale="locale"
                :order="order"
                :order-id="orderId"
                :remove-item-state="removeItemState"
                vs
              />
              <product-header v-if="sections.vs.petOwner.length">
                <template #icon>
                  <fa-icon :icon="['far', 'eye']" class="icon mr-1" />
                </template>
                <template #title>{{ $t('basket.vsGuardianTitle') }}</template>
              </product-header>
              <product
                v-for="(item, index) in sections.vs.petOwner"
                :key="item.id"
                :item="item"
                :last="index + 1 === sections.vs.petOwner.length"
                :locale="locale"
                :order="order"
                :order-id="orderId"
                :remove-item-state="removeItemState"
                vs
              />
              <hr />
            </section>

            <section v-if="!orderId || !order" id="additional-info">
              <!-- eslint-disable-next-line vue/no-v-html (Make sure it's always hard-coded) -->
              <h3 v-html="$t('sideBasket.info')" />
            </section>

            <section id="discount-code">
              <discount ref="discount-code" :discount-state="discountState" :order="order" @applyDiscount="applyDiscount" />
              <section v-if="removeItemState && !removeItemState.loading && removeItemState.error" id="remove-item-errors" class="mt-2">
                <h4 class="text-danger">{{ removeItemState.error }}</h4>
              </section>
              <hr />
            </section>

            <section id="order-summary">
              <totals :order="order" :sections="sections">
                <template #above-total>
                  <h2 id="bundle-save" class="my-3 py-2 text-center" hidden>
                    <a class="text-secondary" href="javascript:void(0)" @click="hide">
                      <span v-if="bundleCount < 3">{{ $t(`basket.addAnother[${bundleCount}]`) }}</span>
                      <span v-else>{{ $t(`basket.addAnother[3]`) }}</span>
                    </a>
                  </h2>
                </template>
                <template #below-total>
                  <h2 v-if="discountMessage" class="ml-3 mt-2 text-success">
                    {{ discountMessage }}
                  </h2>
                </template>
              </totals>
              <b-button block class="mt-2" href="javascript:void(0)" variant="outline-primary" @click="hide">
                {{ $t('sideBasket.continueShopping') }}
              </b-button>
              <b-button
                v-if="order.id"
                v-testid="'sidebar-checkout-action'"
                :disabled="disableCheckout"
                block
                class="mt-3"
                type="submit"
                variant="primary"
                @click="checkoutClick"
              >
                {{ $t('sideBasket.checkout') }}
              </b-button>
              <b-button v-else v-testid="'sidebar-checkout-action'" :href="login" block class="mt-3" variant="primary">
                {{ $t('sideBasket.checkout') }}
              </b-button>
              <div class="text-center mt-3 pb-5">
                <b-link href="javascript:void(0)" @click="cancelOrderClick">
                  {{ $t('sideBasket.cancelOrder') }}
                </b-link>
              </div>
            </section>
          </section>
        </section>
      </template>
    </b-sidebar>
  </section>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { CookieMixins } from '@/mixins/cookie';
import { DpLoading, DpProductMessaging } from '@docupet/component-library';
import baseLocaleUrl from '@/mixins/baseLocaleUrl';
import PetHeader from './PetHeader';
import ProductHeader from './basket/ProductHeader';
import Product from './basket/Product';
import PetFooter from './basket/PetFooter';
import NextPetTagCTA from './basket/NextPetTagCTA';
import Totals from './basket/Totals';
import Discount from './basket/Discount';
import { NotificationMixins } from '@/mixins/notification';
import configureWalletCard from '@/components/basket/ConfigureWalletCard';

export default {
  name: 'SideBasket',
  components: {
    DpLoading,
    Product,
    ProductHeader,
    PetHeader,
    PetFooter,
    NextPetTagCTA,
    Totals,
    Discount,
    configureWalletCard,
    DpProductMessaging,
  },
  mixins: [baseLocaleUrl, CookieMixins, NotificationMixins],
  props: {
    value: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      windowWidth: window.innerWidth,
    };
  },
  computed: {
    ...mapState({
      locale: (state) => state.locale,
      cityId: (state) => state.cityId,
      orderId: (state) => state.orderId,
      hasToken: (state) => !!state.token,
      userOrder: (state) => state.order.order,
      addItemState: (state) => state.basket.addItem,
      discountState: (state) => state.basket.discount,
      removeItemState: (state) => state.basket.removedItem,
      glowTagProducts: (state) => state.otherProduct.glowTagProducts,
      connectorProducts: (state) => state.otherProduct.connectorProducts,
      orderLoading: (state) =>
        state.user.loading ||
        state.user.cityLoading ||
        state.order.loading ||
        state.otherProduct.loading ||
        (state.basket.loading && !state.basket.discount?.loading),
    }),
    ...mapGetters({
      pets: 'basket/getPets',
      sections: 'basket/getSections',
      generateFullUrl: 'generateFullUrl',
      renewalPetsInOrder: 'basket/renewalPetsInOrder',
      nextAccountPetWithoutTag: 'basket/nextAccountPetWithoutTag'
    }),
    showBasket() {
      return this.value;
    },
    order() {
      return this.userOrder;
    },
    bundleCount() {
      let count = 0;
      this.order?.items?.forEach((item) => {
        if (item.product?.type === 'DESIGNER_TAG' && Number(item.product?.price) > 0) {
          count++;
        }
      });
      return count;
    },
    bundleCountByPetId() {
      if (this.pets.length === 0) {
        return Object.assign({});
      }
      // map # of matching items to pet ID
      return this.pets.reduce(
        (accumulator, pet) => {
          accumulator[pet.id] = this.order?.items?.filter(
            (orderItem) =>
              orderItem.group?.pet?.id == pet.id && orderItem.product.type === 'DESIGNER_TAG' && Number(orderItem.product.price) > 0
          ).length;
          return accumulator;
        },
        {} // initial value, run reduce even if only 1 element in array.
      );
    },
    login() {
      return this.$store.getters.generateFullUrl('/login/store?redirect_path=%2Fstore');
    },
    showWalletCardProducts() {
      return Boolean(this.order?.city?.showWalletCardProducts);
    },
    petCheckoutWalletCardConfigurationUrl() {
      if (!this.order?.groups?.length) return null;

      const configurations = this.order.groups.map((group) => group.walletCardConfigurations).filter(Boolean);

      return configurations.length ? configurations.find((config) => config?.optedIn && config?.url)?.url : null;
    },
    disableCheckout() {
      return this.order?.ableToCheckout === false;
    },
    discountMessage() {
      return this.order.discountAmountContext?.confirmationContext?.success;
    },
    hasWalletCardItemsOrConfiguration() {
      return (petId) => {
        const hasIncompleteConfigs = this.incompleteWalletCardConfigurations(petId).length > 0;
        const hasWalletCards = Boolean(this.sections.pets?.[petId]?.walletcards?.length);
        return hasIncompleteConfigs || hasWalletCards;
      };
    },
  },
  watch: {
    value: function (showSideBasket) {
      if (showSideBasket) {
        this.$store.commit('basket/DISCOUNTERROR', false);
        this.loadBasket();
        this.$nextTick(() => {
          document.getElementById('basket-sidebar')?.focus();
        });
      }
    },
  },
  async updated() {
    this.windowWidth = window.innerWidth;
  },
  methods: {
    async loadBasket(code = null, fetchOrder = false) {
      await this.$store.dispatch('otherProduct/fetchConnectorProducts', { cityId: this.cityId }, { root: true });
      await this.$store.dispatch('otherProduct/fetchGlowTagProduct', null, { root: true });
      if (!this.hasToken && !this.orderId) {
        const data = await this.splitSetBasket();
        const discountCode = code || this.getBasketCodeCookie();
        await this.$store.dispatch('basket/createLocalOrder', {
          cityId: this.cityId,
          data,
          discountCode,
          changingDiscount: !!code,
          onSuccessSetOrder: true,
        });
      } else if (fetchOrder) {
        await this.$store.dispatch('order/fetchOrder', { orderId: this.orderId });
      }
    },
    hideBasket() {
      this.$store.commit('basket/SHOW_BASKET', false);
    },
    async applyDiscount(code) {
      if (this.hasToken && this.order.id) {
        if (this.order.discount) {
          this.$store.dispatch('basket/removeDiscount', { orderId: this.orderId }).then(() => {
            this.removeBasketCodeCookie();
            this.$refs?.['discount-code']?.removeDiscount();
          });
        } else {
          await this.$store.dispatch('basket/applyDiscount', { orderId: this.orderId, code });
        }
      } else {
        const data = await this.splitSetBasket();
        this.$store
          .dispatch('basket/createLocalOrder', {
            cityId: this.cityId,
            data,
            discountCode: code,
            changingDiscount: true,
            onSuccessSetOrder: true,
          })
          .then(() => {
            this.setBasketCodeCookie(code);
            this.$refs?.['discount-code']?.removeDiscount();
          });
      }
    },
    async onAddChildItem({ item, child, personalization, parentItem, personalizationName, personalizationPhone }) {
      let data = await this.splitSetBasket();
      const discountCode = await this.getBasketCodeCookie();
      if (!this.hasToken || !this.orderId) {
        if (child) {
          const index = data.findIndex((d) => d.productId == parentItem.product?.id);
          if (index !== -1) {
            if (personalization) {
              data[index].personalize.selected = true;
              data[index].personalize.name = personalizationName;
              data[index].personalize.phone = personalizationPhone;
            } else {
              if (item.product?.type === 'CONNECTOR') {
                data[index].connector = item.product?.id;
                data[index].isSplitRingConnector = false;
              } else if (item.keyChain) {
                data[index].keyChain = true;
              } else {
                data[index].otherProductIds?.push(item.product?.id);
              }
            }
          }
        }
      }

      this.$store
        .dispatch('basket/addItemOrPersonalization', {
          vm: this,
          orderId: this.orderId,
          itemId: parentItem?.id,
          productDetails: {
            parentId: parentItem?.id,
            productId: item?.product?.id,
            keyChain: item?.keyChain,
          },
          personalization: personalization ? { name: personalizationName, phone: personalizationPhone } : false,
          data,
          discountCode,
          changingDiscount: false,
        })
        .then(() => {
          if (!this.hasToken && !this.orderId) this.setBasketCookie(data);

          // Track event for analytics
          if (window?.dataLayer) {
            window?.dataLayer?.push({
              ecommerce: null,
            });
            const items = [];
            if (parentItem || personalization) {
              const _itemData = {
                quantity: 1,
                coupon: this.order?.discount?.code || '',
                currency: `${this.order?.city?.country || 'US'}D`,
                item_list_id: parentItem?.product?.style?.id || '',
                item_list_name: parentItem?.product?.style?.code?.toUpperCase() || '',
              };
              if (personalization) {
                items.push({
                  ..._itemData,
                  item_id: `${parentItem?.product?.type?.toUpperCase()}_PERSONALIZATION`,
                  item_name: `${parentItem?.product?.type?.toUpperCase()}_PERSONALIZATION`,
                  item_category: 'PERSONALIZATION',
                  price: Number(parentItem?.product?.personalization?.price || 0).toFixed(2),
                });
              } else {
                const _childData = {
                  ..._itemData,
                  item_id: item?.product?.id || '',
                  item_name: item?.product?.name?.toUpperCase() || '',
                  item_category: item?.product?.type?.toUpperCase() || '',
                  price: Number(item?.product?.price || 0).toFixed(2),
                };
                if (['OTHER'].includes(item?.product?.type) || (item?.product?.type === 'CONNECTOR' && item?.product?.id !== '207')) {
                  items.push(_childData);
                }
                if (item?.product?.type === 'DESIGNER_TAG' && item?.keyChain) {
                  items.push({
                    ..._childData,
                    item_id: `${item?.product?.id}_MATCHING_KEYCHAIN`,
                    item_name: `${item?.product?.id}_MATCHING_KEYCHAIN`,
                    item_category: 'MATCHING_KEYCHAIN',
                    price: Number(item?.product?.keyChainDiscountedPrice || item?.product?.keyChainPrice || 0).toFixed(2),
                  });
                }
              }
            }
            window.dataLayer.push({
              event: 'add_to_cart',
              ecommerce: {
                items: items,
                currency: `${this.order?.city?.country || 'US'}D`,
                value: Number(items?.reduce((p, c) => p + Number(c.price), 0)).toFixed(2),
              },
              location: 'user_basket',
            });
          }
        })
        .catch(async () => {
          if (!this.hasToken && !this.orderId) await this.setBasketCookie(await this.splitSetBasket());
        });
    },
    async onRemoveItem({ item, child, personalization, quantity = 1 }) {
      if (item.product.type === 'PET_TRACKING_DEVICE') {
        this.$emit('remove-pet-tracker');
      }
      let data = await this.splitSetBasket();
      const discountCode = await this.getBasketCodeCookie();
      if (!this.hasToken || !this.orderId) {
        if (child) {
          if (personalization) {
            let index = data.findIndex((d) => {
              return (
                d.productId == item.product?.id &&
                !!d.personalize.selected == !!item.customTagOrderInfo &&
                (!d.personalize.selected ||
                  (d.personalize?.name == item.customTagOrderInfo?.name && d.personalize?.phone == item.customTagOrderInfo?.phone))
              );
            });
            if (index != -1) {
              data[index].personalize.selected = false;
            }
          } else {
            if (item.product?.type === 'CONNECTOR') {
              let index = data.findIndex((d) => {
                return (
                  d.connector == item.product.id &&
                  item.parent &&
                  d.productId == item.parent.product.id &&
                  !!d.personalize.selected == !!item.parent.customTagOrderInfo
                );
              });
              if (index != -1) {
                const splitRingConnector = this.connectorProducts?.find((p) => p.price === 0);
                data[index].connector = splitRingConnector?.id || '';
                data[index].isSplitRingConnector = true;
              }
            } else if (item?.keyChain) {
              let index = data.findIndex((d) => {
                return (
                  item.parent &&
                  d.productId == item.parent.product.id &&
                  !!d.personalize.selected == !!item.parent.customTagOrderInfo &&
                  !!d.keyChain == !!item.keyChain
                );
              });
              if (index != -1) {
                data[index].keyChain = false;
              }
            } else {
              let index = data.findIndex((d) => {
                return (
                  d.otherProductIds.includes(item.product.id) &&
                  item.parent &&
                  d.productId == item.parent.product.id &&
                  !!d.personalize.selected == !!item.parent.customTagOrderInfo
                );
              });
              if (index != -1) {
                data[index].otherProductIds?.splice(
                  data[index].otherProductIds?.findIndex((id) => id == item.product.id),
                  1
                );
              }
            }
          }
        } else {
          if (item.product.type === 'DESIGNER_TAG') {
            data.splice(
              data.findIndex(
                (d) =>
                  d.productId == item.product.id &&
                  d.connector == (item.children[0] ? item.children[0].product.id : '') &&
                  !!d.personalize.selected == !!item.customTagOrderInfo
              ),
              1
            );
          } else {
            let q = quantity;
            while (q > 0) {
              data.splice(
                data.findIndex((d) => d.productId === item.product.id),
                1
              );
              q--;
            }
          }
        }
      }

      this.$store
        .dispatch('basket/removeItemOrPersonalization', {
          vm: this,
          orderId: this.orderId,
          itemId: item.id,
          personalization,
          data,
          discountCode,
          changingDiscount: false,
          productId: item.product?.id,
          quantity,
        })
        .then(() => {
          if (!this.hasToken && !this.orderId) this.setBasketCookie(data);
        })
        .catch(async (e) => {
          console.error(e);
          if (!this.hasToken && !this.orderId) await this.setBasketCookie(await this.splitSetBasket());
        });
    },
    noNewTag(item) {
      return item.product.name && item.product.name.toLowerCase().includes('no new tag');
    },
    removeTag(item, pet) {
      // if license and more then one tag, or replacement and not municipal
      return (
        (!this.sections.pets[pet.id].licenses.length && !this.sections.pets[pet.id].replacements.length) ||
        (!!this.sections.pets[pet.id].licenses.length && this.sections.pets[pet.id].tags.length > 1) ||
        (!!this.sections.pets[pet.id].replacements.length && !item.product.municipalTag) ||
        this.containFreePetTagWithReplacementFee(pet.id)
      );
    },
    editTag(item, pet) {
      // if license and more then one tag, or replacement and not municipal
      return !this.removeTag(item, pet) && !(!!this.sections.pets[pet.id].replacements.length && item.product.municipalTag);
    },
    cancelOrderClick() {
      if (window.dataLayer) {
        window?.dataLayer?.push({
          ecommerce: null,
        });
        window.dataLayer.push({
          event: 'remove_from_cart',
          ...this.getAnalyticsVars(),
          location: 'user_side_basket',
        });
      }
      if (this.orderId) {
        this.$store.dispatch('basket/cancelOrder', this.orderId);
      } else {
        this.removeBasketCookie();
        this.removeBasketCodeCookie();
        window.location.reload();
      }
    },
    checkoutClick() {
      if (!this.hasToken && !this.orderId) {
        window.location.href = this.generateFullUrl(`/login/store?redirect_path=%2Fstore`);
      } else if (this.petCheckoutWalletCardConfigurationUrl) {
        window.location.href = `${this.petCheckoutWalletCardConfigurationUrl}?redirect_path=basket`;
      } else {
        window.location.href = this.generateFullUrl(`/order/${this.orderId}/checkout`);
      }
      if (window.dataLayer) {
        window?.dataLayer?.push({
          ecommerce: null,
        });
        window.dataLayer.push({
          event: 'begin_checkout',
          ...this.getAnalyticsVars(),
          location: 'user_side_basket',
        });
      }
    },
    getAnalyticsVars() {
      if (window.dataLayer) {
        window?.dataLayer?.push({
          ecommerce: null,
        });
        const items = [];
        this.order?.items?.forEach((item) => {
          if (item.parent) return;
          const _itemData = {
            quantity: 1,
            coupon: this.order?.discount?.code || '',
            currency: `${this.order?.city?.country || 'US'}D`,
            item_list_id: item?.product?.style?.id || '',
            item_list_name: item?.product?.style?.code?.toUpperCase() || '',
          };
          item?.children?.forEach((child) => {
            const _childData = {
              ..._itemData,
              item_id: child.product?.id || '',
              item_name: child.product?.name?.toUpperCase() || '',
              item_category: child.product?.type?.toUpperCase() || '',
              price: Number(child.subtotal || 0).toFixed(2),
            };
            if (['OTHER'].includes(child.product?.type) || (child.product?.type === 'CONNECTOR' && child.product?.id !== '207')) {
              items.push(_childData);
            }
            if (child.product?.type === 'DESIGNER_TAG' && child.keyChain) {
              items.push({
                ..._childData,
                item_id: `${item?.product?.id}_MATCHING_KEYCHAIN`,
                item_name: `${item?.product?.id}_MATCHING_KEYCHAIN`,
                item_category: 'MATCHING_KEYCHAIN',
              });
            }
          });
          if (item?.customTagOrderInfo || item?.customization) {
            items.push({
              ..._itemData,
              item_id: `${item?.product?.type?.toUpperCase()}_PERSONALIZATION`,
              item_name: `${item?.product?.type?.toUpperCase()}_PERSONALIZATION`,
              item_category: 'PERSONALIZATION',
              price: Number(item?.customization || 0).toFixed(2),
            });
          }
          items.push({
            ..._itemData,
            item_id: item?.product?.id || '',
            item_name: item?.product?.name?.toUpperCase() || '',
            item_category: item?.product?.type?.toUpperCase() || '',
            price: Number(item?.subtotal || 0).toFixed(2),
          });
        });
        return {
          ecommerce: {
            items: items,
            currency: `${this.order?.city?.country || 'US'}D`,
            value: Number(items?.reduce((p, c) => p + Number(c.price), 0)).toFixed(2),
            coupon: this.order?.discount?.code || '',
          },
        };
      }
    },
    licensingTotalByPet(petId) {
      return this.sections?.pets?.[petId]?.licenses?.reduce((acc, item) => acc + item.subtotal, 0) || 0;
    },
    incompleteWalletCardConfigurations(petId) {
      const group = this.order?.groups?.find((group) => group.pet?.id === petId);

      if (!group) {
        return [];
      }

      return group.walletCardConfigurations.filter((configuration) => {
        return configuration.optedIn && !configuration.completed;
      });
    },
    containFreePetTagWithReplacementFee(petId) {
      return Boolean(this.order?.groups?.find((group) => group.pet?.id === petId)?.freePetTagContext?.containsFreePetTagWithReplacementFee);
    },
    freePetTagGroupNotification(petId) {
      return this.order?.groups?.find((group) => group.pet?.id === petId)?.freePetTagContext?.groupNotificationText;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@docupet/component-library/src/assets/styles/_variables';
#basket-empty {
  div {
    margin-top: 22vh;
    margin-bottom: 22vh;
  }

  svg {
    color: $dp-gray-4;
    width: 50%;
    height: 30%;
  }

  h2 {
    color: $dp-gray-5;
  }
}
.bg-warning-light {
  background-color: $dp-light-yellow;
}
</style>
